import { useMemo } from 'react';
import { useAppData } from '../context/AppDataContext';
import { useAuth, type Role } from '../context/AuthContext';
import type { Client, ContentItem, Deliverable, Project, Task } from '../data/types';

/**
 * Partners see the whole studio; everybody else sees their own work.
 *
 * Centralised here so that a page added next month cannot forget the check by
 * reading `useAppData()` directly. It is a convenience, not a control — the
 * server has already decided what it was willing to send, and this only
 * decides which of that to put in front of somebody.
 */
export function hasFullVisibility(role: Role | undefined): boolean {
  return role === 'PARTNER' || role === 'MANAGER';
}

interface ScopedWork {
  /** True when this person is only being shown their own work. */
  isScoped: boolean;
  /** Which person on the roster they are, if any. */
  personaId: string | null;
  tasks: Task[];
  deliverables: Deliverable[];
  content: ContentItem[];
  projects: Project[];
  /** The clients they have work for — the whole roster for a partner. */
  clients: Client[];
}

export function useScopedWork(): ScopedWork {
  const { tasks, deliverables, content, projects, clients } = useAppData();
  const { currentUser } = useAuth();

  return useMemo(() => {
    const personaId = currentUser?.linkedPersonaId ?? null;

    if (currentUser === null || hasFullVisibility(currentUser.role)) {
      return { isScoped: false, personaId, tasks, deliverables, content, projects, clients };
    }

    if (personaId === null) {
      // A real login that is not anybody on the roster — a service token, or
      // an account nobody has linked yet. Showing nothing is the honest
      // answer; falling back to everyone else's work is how a "my tasks"
      // screen quietly becomes the whole studio's.
      return {
        isScoped: true,
        personaId: null,
        tasks: [],
        deliverables: [],
        content: [],
        projects: [],
        clients: []
      };
    }

    // The server has already narrowed everything to the clients this person is
    // on (see src/api/scope.ts), so what arrived is exactly what they may see:
    // their clients, all the work on them, and any task given to them. Filtering
    // again here to only their own pieces hid the rest of the work on their
    // clients, which is the thing a person on an account needs to see.
    return { isScoped: true, personaId, tasks, deliverables, content, projects, clients };
  }, [currentUser, tasks, deliverables, content, projects, clients]);
}
